import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import VehicleRegister from "./VehicleRegister";
import VehicleUpdate from "./VehicleUpdate";
import { 
  Car, 
  Truck, 
  X, 
  Search, 
  Edit2, 
  CheckCircle, 
  XCircle,
  MapPin,
  Weight,
  User,
  Calendar,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Grid,
  Table
} from "lucide-react";
import { fetchVehicles as fetchVehiclesList } from "../actions/VehicleActions";
import TrailerIcon from "../assets/trailor.jpg";
import CraneImage from "../assets/crane.jpg";
import forklift from "../assets/fork lift.png"

// Vehicle Type Icons Mapping
const VEHICLE_ICONS = {
  Car: { icon: Car, color: "blue", label: "Car" },
  Forklift: { icon: Truck, color: "yellow", label: "Forklift", image: forklift },
  Van: { icon: Truck, color: "green", label: "Van" },
  Lorry: { icon: Truck, color: "orange", label: "Lorry" },
  SUV: { icon: Car, color: "purple", label: "SUV" },
  Cab: { icon: Car, color: "indigo", label: "Cab" },
  Bus: { icon: Truck, color: "red", label: "Bus" },
  "Cherry Picker": { icon: Truck, color: "pink", label: "Cherry Picker" },
  Trailer: { icon: Truck, color: "teal", label: "Trailer", image: TrailerIcon },
  Crane: { icon: Truck, color: "gray", label: "Crane", image: CraneImage },
};

// Stat Card Component
function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200" },
    green: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200" },
    red: { bg: "bg-red-50", iconBg: "bg-red-100", text: "text-red-600", border: "border-red-200" },
    gray: { bg: "bg-gray-50", iconBg: "bg-gray-100", text: "text-gray-600", border: "border-gray-200" },
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className={`${colors.iconBg} p-1.5 rounded-lg`}>
          <Icon size={14} className={colors.text} />
        </div>
      </div>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600 mt-0.5">{title}</p>
    </div>
  );
}

// Vehicle Type Card Component
function VehicleTypeCard({ type, count, meta }) {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200",
    green: "bg-emerald-50 border-emerald-200",
    yellow: "bg-yellow-50 border-yellow-200",
    orange: "bg-orange-50 border-orange-200",
    purple: "bg-purple-50 border-purple-200",
    indigo: "bg-indigo-50 border-indigo-200",
    red: "bg-red-50 border-red-200",
    pink: "bg-pink-50 border-pink-200",
    teal: "bg-teal-50 border-teal-200",
    gray: "bg-gray-50 border-gray-200",
  };
  
  const Icon = meta.icon;
  const bgColor = colorClasses[meta.color] || colorClasses.gray;
  
  return (
    <div className={`${bgColor} border rounded-lg p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-center gap-2 mb-1">
        {meta.image ? (
          <img src={meta.image} alt={type} className="w-6 h-6 object-contain rounded" />
        ) : (
          <Icon size={14} className={`text-${meta.color}-600`} />
        )}
        <span className="text-xs font-medium text-gray-700">{type}</span>
      </div>
      <p className="text-lg font-semibold text-gray-900">{count}</p>
    </div>
  );
}

// Vehicle Card Component (Mobile View)
function VehicleCard({ vehicle, meta, onEdit }) {
  const statusColor = vehicle.status === "Active" 
    ? "bg-emerald-50 text-emerald-700" 
    : "bg-gray-100 text-gray-600";
  
  const StatusIcon = vehicle.status === "Active" ? CheckCircle : XCircle;
  
  return (
    <div className="bg-white border border-gray-100 rounded-lg p-3 hover:shadow-sm transition">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gray-50 rounded-lg">
            {meta.image ? (
              <img src={meta.image} alt={vehicle.vehicleType} className="w-5 h-5 object-contain" />
            ) : (
              <meta.icon size={14} className={`text-${meta.color}-600`} />
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{vehicle.vehicleId}</p>
            <p className="text-xs text-gray-500">{vehicle.vehicleType}</p>
          </div>
        </div>
        <button
          onClick={() => onEdit(vehicle.vehicleId)}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition"
        >
          <Edit2 size={12} />
        </button>
      </div>
      
      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-gray-500">Weight</p>
          <p className="font-medium text-gray-900">{vehicle.weightCat || "-"}</p>
        </div>
        <div>
          <p className="text-gray-500">Driver</p>
          <p className="font-medium text-gray-900">{vehicle.driverSno || "-"}</p>
        </div>
        <div>
          <p className="text-gray-500">Booking</p>
          <p className="font-medium text-gray-900">{vehicle.bStatus || "-"}</p>
        </div>
        <div>
          <p className="text-gray-500">Location</p>
          <p className="font-medium text-gray-900">
            {vehicle.latitude && vehicle.longitude 
              ? `${vehicle.latitude}, ${vehicle.longitude}`
              : "-"}
          </p>
        </div>
      </div>
      
      {/* Status */}
      <div className="mt-2 pt-2 border-t border-gray-50">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
          <StatusIcon size={10} />
          {vehicle.status}
        </span>
      </div>
    </div>
  );
}

export default function AdminVehicles() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');
  const recordsPerPage = 10;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authKey } = useSelector((state) => state.auth);
  const { items: vehicles, loading, error } = useSelector((state) => state.vehicle);

  // Fetch Vehicles
  const fetchVehicles = useCallback(async () => {
    await dispatch(fetchVehiclesList());
  }, [dispatch]);

  useEffect(() => {
    if (authKey) fetchVehicles();
  }, [authKey, fetchVehicles]);

  // Detect screen size for default view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('card');
      } else {
        setViewMode('table');
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClearFilters = () => {
    setSearchQuery("");
    setVehicleTypeFilter("");
    setStatusFilter("");
  };

  // Summary Stats
  const totalCount = vehicles.length;
  const activeCount = vehicles.filter((v) => v.status === "Active").length;
  const inactiveCount = totalCount - activeCount;

  // Type Counts
  const typeCounts = useMemo(() => {
    const counts = {};
    vehicles.forEach((v) => {
      const t = v.vehicleType;
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [vehicles]);

  // Filtering
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      const textMatch = 
        (v.vehicleId || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (v.vehicleType || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (v.driverSno || "").toLowerCase().includes(searchQuery.toLowerCase());

      const typeMatch = vehicleTypeFilter === "" || v.vehicleType === vehicleTypeFilter;
      const statusMatch = statusFilter === "" || v.status === statusFilter;

      return textMatch && typeMatch && statusMatch;
    });
  }, [vehicles, searchQuery, vehicleTypeFilter, statusFilter]);

  useEffect(() => setCurrentPage(1), [searchQuery, vehicleTypeFilter, statusFilter]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredVehicles.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredVehicles.length / recordsPerPage);

  const handleAddVehicle = () => setIsRegisterModalOpen(true);
  const handleEditVehicle = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    setIsUpdateModalOpen(true);
  };

  const vehicleTypes = Object.keys(VEHICLE_ICONS);
  const statusOptions = ["Active", "Inactive"];

  const statusColorClass = (status) =>
    status === "Active"
      ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium"
      : "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium";

  return (
    <AdminLayout>
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              Vehicle Management
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage fleet vehicles, track status and assignments
            </p>
          </div>
          <button
            onClick={handleAddVehicle}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition shadow-sm"
          >
            <Car size={14} />
            Add Vehicle
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCard title="Total Vehicles" value={totalCount} icon={Car} color="blue" />
        <StatCard title="Active" value={activeCount} icon={CheckCircle} color="green" />
        <StatCard title="Inactive" value={inactiveCount} icon={XCircle} color="gray" />
      </div>

      {/* Vehicle Type Cards */}
      <div className="mb-4">
        <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Vehicle Types
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {vehicleTypes.map((type) => (
            <VehicleTypeCard
              key={type}
              type={type}
              count={typeCounts[type] || 0}
              meta={VEHICLE_ICONS[type]}
            />
          ))}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Vehicle ID, Type, or Driver..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
          />
        </div>
        
        <div className="flex gap-2">
          <select
            value={vehicleTypeFilter}
            onChange={(e) => setVehicleTypeFilter(e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
          >
            <option value="">All Types</option>
            {vehicleTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        
        {/* View Toggle - Only visible on desktop */}
        <div className="hidden sm:flex gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'table' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table size={12} className="inline mr-1" />
            Table
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'card' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Grid size={12} className="inline mr-1" />
            Cards
          </button>
        </div>
      </div>

      {/* Filter Summary */}
      {(searchQuery || vehicleTypeFilter || statusFilter) && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500">
            Showing {filteredVehicles.length} of {vehicles.length} vehicles
          </p>
          <button
            onClick={handleClearFilters}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <X size={12} />
            Clear filters
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={16} className="text-gray-400 animate-spin" />
            <span className="ml-2 text-xs text-gray-500">Loading vehicles...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="bg-red-50 text-red-600 text-xs p-2 rounded-lg inline-block">
              {error}
            </div>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-8">
            <div className="flex flex-col items-center gap-1.5">
              <Car size={24} className="text-gray-300" />
              <p className="text-xs text-gray-500">No vehicles found</p>
              <button
                onClick={handleAddVehicle}
                className="mt-1 text-xs text-gray-900 hover:text-gray-700 underline"
              >
                Add your first vehicle
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Table View - Desktop */}
            <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle ID
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weight
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Driver
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {currentRecords.map((vehicle) => {
                    const meta = VEHICLE_ICONS[vehicle.vehicleType] || VEHICLE_ICONS.Car;
                    const StatusIcon = vehicle.status === "Active" ? CheckCircle : XCircle;
                    
                    return (
                      <tr key={vehicle.vehicleId} className="hover:bg-gray-50/50 transition">
                        <td className="py-2.5 px-3">
                          <span className="text-sm font-medium text-gray-900">
                            {vehicle.vehicleId}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1.5">
                            {/* {meta.image ? (
                              <img src={meta.image} alt={vehicle.vehicleType} className="w-5 h-5 object-contain" />
                            ) : (
                              <meta.icon size={14} className={`text-${meta.color}-600`} />
                            )} */}
                            <span className="text-sm text-gray-700">{vehicle.vehicleType}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-sm text-gray-600">
                          {vehicle.weightCat || "-"}
                        </td>
                        <td className="py-2.5 px-3 text-sm text-gray-600">
                          {vehicle.driverSno || "-"}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            vehicle.status === "Active" 
                              ? "bg-emerald-50 text-emerald-700" 
                              : "bg-gray-100 text-gray-600"
                          }`}>
                            <StatusIcon size={10} />
                            {vehicle.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          {vehicle.latitude && vehicle.longitude ? (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <MapPin size={10} />
                              {vehicle.latitude}, {vehicle.longitude}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <button
                            onClick={() => handleEditVehicle(vehicle.vehicleId)}
                            className="inline-flex items-center gap-1 px-1.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition"
                          >
                            <Edit2 size={12} />
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Card View - Mobile */}
            <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-3 space-y-2`}>
              {currentRecords.map((vehicle) => (
                <VehicleCard
                  key={vehicle.vehicleId}
                  vehicle={vehicle}
                  meta={VEHICLE_ICONS[vehicle.vehicleType] || VEHICLE_ICONS.Car}
                  onEdit={handleEditVehicle}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {filteredVehicles.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredVehicles.length)} of {filteredVehicles.length}
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 py-1 text-xs text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <VehicleRegister 
        isOpen={isRegisterModalOpen} 
        onClose={() => setIsRegisterModalOpen(false)} 
        onSuccess={fetchVehicles}
      />

      <VehicleUpdate
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        vehicleId={selectedVehicleId}
        onSuccess={fetchVehicles}
      />
    </AdminLayout>
  );
}